/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import ActionButton from '@components/navbar/ActionButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import { hasNoAccess } from '@global/helpers';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetPlanogramListQuery,
  useGetPlanogramCountQuery,
  useGetPlanogramInfiniteListInfiniteQuery,
} from '@api/listApi';

const ADD_ROLES_BLOCKLIST = [
  'salesman',
  'sales_supervisor',
  'branch_head',
  'finance_supervisor',
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'national_key_account_manager',
  'vice_president_finance',
  'budget_controller',
  'finance_head',
  'tax_support',
  'billing_supervisor',
  'general_manager_marketing',
  'marketing_support',
  'collector',
];

const tableStructure = {
  account_group: {
    head: 'Account Group',
    body: 'account_group.code',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  total_attached_files: {
    head: 'Total Attached Files',
    body: 'total_attached_files',
    type: 'slot',
  },
  updated_at: { head: 'Last Update', body: 'updated_at', type: 'date' },
};

// Search-only: the search box writes `account_group.code`; unwrap to a $regex query.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const code = raw['account_group.code']?.$regex ?? raw['account_group.code'];
  if (code) {
    filter['account_group.code'] = { $regex: code, $options: 'i' };
  }
  return filter;
};

const PlanogramScreen = (props) => {
  // ── derived ──
  const showAddButton = hasNoAccess(ADD_ROLES_BLOCKLIST);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetPlanogramListQuery,
    countHook: useGetPlanogramCountQuery,
    infiniteHook: useGetPlanogramInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.['account_group.code'];
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── handlers ──
  const goToForm = () => {
    openChild('PlanogramFormScreen');
  };

  const gotToDetail = ({ data }) => {
    openChild('PlanogramDetailScreen', {
      titleScreen: data.account_group?.code || '',
      name: data.account_group?.code || '',
      id: data._id,
    });
  };

  // ── effects ──
  // Own the header so the mobile Add goes through openChild (preserves the list
  // on return) rather than the navigator's plain navigate.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : showAddButton ? (
          <ActionButton
            icon={require('@assets/img/icons/plus-white.svg')}
            onCallback={goToForm}
          />
        ) : (
          false
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, showAddButton]);

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Planogram">
            {showAddButton ? (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={() => goToForm()}
                />
              </View>
            ) : (
              false
            )}
          </Layout.Title>

          <Table
            filterName={scope}
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasCallback={true}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            showFilter={false}
            searchKey="account_group.code"
            searchPlaceholder="Search by Account Group..."
            onClickRow={(val) => gotToDetail(val)}
            searchValue={searchValue}
            slot={{
              total_attached_files: ({ data, index }) => (
                <SociollaText key={index}>
                  {data.shelves && data.shelves.length
                    ? data.shelves.length
                    : '0'}{' '}
                  {data.shelves && data.shelves.length > 1 ? 'files' : 'file'}
                </SociollaText>
              ),
            }}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="account_group.code"
          placeholder="Search by Account Group"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          skip={params.skip}
          limit={params.limit}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              onPress={() => gotToDetail({ data: data })}>
              <SociollaText type="medium">
                {data?.account_group?.code ? data.account_group.code : '-'}
              </SociollaText>
              <SociollaText style={{ fontSize: 12 }}>
                {data.shelves?.length ? data.shelves.length : '0'} Attached File
                {data.shelves?.length > 1 ? 's' : ''}
              </SociollaText>
            </Layout.ItemMobile>
          )}
        />
      )}
    </Layout.Container>
  );
};

export default PlanogramScreen;
